import { useEffect, useRef, useState } from 'react'

import { InjectionTokens } from '@/controller/tokens'

import { UserChartSynced } from '@/domain/events/UserChartSynced'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'
import { EnsureUserChartTilesUseCase } from '@/domain/useCases/UserCharts/EnsureUserChartTilesUseCase'

import { hasUserChartTiles } from '@/data/UserChartRepository/userChartTileStore'

import { MapChartOpacitySlider } from '../components/MapChartOpacitySlider'
import { MapRasterLayer } from '../components/MapView/MapRasterLayer'
import { MapRasterSource } from '../components/MapView/MapRasterSource'
import { stackIds } from './MapStackPresenter'

import type { ILogger } from '@/infra/logger/ILogger'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'
import { GlobalToastUtil } from '@/utils/globalToastUtil'

// downloadProcessed maps a server non-2xx (e.g. the chart was removed) to this
// code, while transient transport failures surface as NETWORK_ERROR. Only the
// former means the chart is gone for good and should be dropped from the map.
const CHART_REMOVED_ERROR = 'DOWNLOAD_FAILED'

function sameChartIds(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of b) if (!a.has(id)) return false
  return true
}

// Renders each active user chart as a georeferenced raster overlay. The tiles
// are served from the in-memory tile store through the map's transformRequest
// (see userChartTransformRequest), so the source only needs the sentinel
// `tilesUrlTemplate` baked into each UserChartInfo.
//
// On reload the active charts are restored from persisted MapLayerState, but the
// in-memory tile store is empty — so we rehydrate each chart's tiles (from
// IndexedDB, falling back to the network) before rendering its source. Rendering
// is gated on readiness so Mapbox does not cache the transparent fallback tile
// for a source created while the store is still empty.
function MapUserChartsPresenter() {
  const { t } = useI18n()
  const logger = useInjection<ILogger>(InjectionTokens.Logger)
  const [mapLayerState, mapLayerStateMutator] = useGlobalState<MapLayerState, MapLayerStateProps>(
    InjectionTokens.MapLayerState
  )
  const [readyChartIds, setReadyChartIds] = useState<Set<string>>(() => new Set())
  // Opacity picked on each chart's slider. Session-only on purpose: a chart that is
  // turned off and on again, or restored on reload, starts fully opaque.
  const [chartOpacities, setChartOpacities] = useState<Record<string, number>>({})

  const activeCharts = mapLayerState.userCharts

  useEffect(() => {
    setChartOpacities((prev) => {
      const activeIds = new Set(activeCharts.map((chart) => chart.id))
      const kept = Object.entries(prev).filter(([id]) => activeIds.has(id))
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept)
    })
  }, [activeCharts])

  // `useI18n` returns a freshly-bound `t` on every render, so it must NOT go into
  // the effect deps below — otherwise the effect re-runs on every render and its
  // setReadyChartIds(new Set()) schedules a re-render, feeding an infinite render
  // loop. Read it through a ref instead (same pattern as MapUserGeometriesPresenter).
  const translateRef = useRef(t)
  translateRef.current = t

  // A chart's tiles may be missing on the server simply because their upload is still
  // pending in the durable outbox (see UserChartSyncService). We only treat a
  // DOWNLOAD_FAILED as "gone for good" once a sync has completed — by then the outbox
  // has been flushed, so a lingering 404 really means the chart was removed remotely.
  const hasSyncedRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    const ensureAll = async () => {
      await Promise.all(
        activeCharts.map(async (chart) => {
          if (hasUserChartTiles(chart.id)) return
          const ensured = await new EnsureUserChartTilesUseCase().execute(chart.id)
          // Drop the overlay only for a chart that is definitively gone on the server,
          // and only after a sync has run (before that a 404 may just mean the tiles
          // upload is still pending). Transient failures keep the chart so a later
          // sync/boot can retry.
          if (!cancelled && ensured.isFailure && ensured.error === CHART_REMOVED_ERROR && hasSyncedRef.current) {
            logger.warn('MapUserCharts', `Active user chart ${chart.id} is no longer available on the server`)
            mapLayerStateMutator.removeActiveUserChart(chart.id)
            GlobalToastUtil.send({
              label: translateRef.current('TOAST_USER-CHART_UNAVAILABLE'),
              variant: 'error',
              dismissible: true
            })
          }
        })
      )

      if (cancelled) return

      // Mark every chart whose tiles are now available so its source renders. Keep
      // the previous Set when the ready ids are unchanged so an unrelated render
      // does not schedule a needless re-render.
      const nextReady = new Set(activeCharts.filter((chart) => hasUserChartTiles(chart.id)).map((chart) => chart.id))
      setReadyChartIds((prev) => (sameChartIds(prev, nextReady) ? prev : nextReady))
    }

    ensureAll()

    // Re-attempt when a sync completes: by then the durable outbox has uploaded any
    // pending tiles and the boot prefetch has pulled them into the store, so a chart
    // that could not render at boot (its tiles were not on the server yet) renders now.
    const subscription = UserChartSynced.subscribe(() => {
      hasSyncedRef.current = true
      if (!cancelled) void ensureAll()
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [activeCharts, logger, mapLayerStateMutator])

  return (
    <>
      {activeCharts.map((chart) => {
        if (!readyChartIds.has(chart.id)) return null

        const sourceId = `user-chart-${chart.id}-source`
        const layerId = `user-chart-${chart.id}-layer`
        const opacity = chartOpacities[chart.id] ?? 1
        const [[west], [east, north]] = chart.bounds

        return (
          <MapRasterSource
            key={chart.id}
            id={sourceId}
            tiles={[chart.tilesUrlTemplate]}
            tileSize={256}
            bounds={[chart.bounds[0][0], chart.bounds[0][1], chart.bounds[1][0], chart.bounds[1][1]]}
            minZoom={chart.minZoom}
            maxZoom={chart.maxZoom}
          >
            <MapRasterLayer
              id={layerId}
              sourceId={sourceId}
              minZoom={chart.minZoom}
              maxZoom={chart.maxZoom}
              opacity={opacity}
              beforeLayerId={stackIds.userCharts}
            />
            <MapChartOpacitySlider
              edge={[
                [west, north],
                [east, north]
              ]}
              value={opacity}
              onChange={(value) => setChartOpacities((prev) => ({ ...prev, [chart.id]: value }))}
              minZoom={chart.minZoom}
              maxZoom={chart.maxZoom}
            />
          </MapRasterSource>
        )
      })}
    </>
  )
}

export { MapUserChartsPresenter }
